import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { truncate } from 'lodash';
import { Alert, Avatar, Button, Dropdown, Layout, Menu, Modal, Space, Typography } from 'antd';
import {
  LogoutOutlined,
  ProfileOutlined,
  ExclamationCircleOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
} from '@ant-design/icons';
import { closeNavigation } from '../../actions/app';
import { logoutUnAuthenticat } from '../../actions/auth';
import { useLocation, useNavigate } from 'react-router-dom';
import Sider from 'antd/es/layout/Sider';
import './Navbar.scss';
import { useAntIcons } from '../../hooks/useIcons';
import TabsRender from './Tabslist';
import BenchPolicyModal from './BenchPolicy';
import moment from 'moment';
import { useSocket } from '../../sockets/WebSocketContext';
import { getData } from '../../actions/apiCall';
import TargetPopup from '../TargetPopup/TargetPopup';
const { Link } = Typography;

const { Content, Header } = Layout;
const { confirm } = Modal;

const Navbar = (props) => {
  const invalidRoutes = ['/pdf'];
  const [collapsed, setCollapsed] = useState(false);
  const [selectedKey, setSelectedKey] = useState('/');
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [openKeys, setOpenKeys] = useState([]);
  const [isBenchPolicy, setBenchPolicy] = useState(false);
  const [targetPopupVisible, setTargetPopupVisible] = useState(false);
  const [employeeTargets, setEmployeeTargets] = useState<any[]>([]);
  const { getIcon } = useAntIcons();
  const location = useLocation();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { socket } = useSocket();
  const { isAuthenticated, user, navigations, routes, tabs } = useSelector((state: any) => ({
    isAuthenticated: state.app.isAuthenticated,
    user: state.app.user,
    tabs: state.app.tabs,
    navigations: state.app.navigations,
    routes: props.routes,
  }));

  useEffect(() => {
    const savedKey = localStorage.getItem('selectedMenuKey');
    const savedOpenKeys = JSON.parse(localStorage.getItem('openMenuKeys')) || [];
    const activeTab = tabs?.find((item) => '/' + item.routeUrl === location.pathname);
    // need to remove tab when profile is selected
    if (activeTab) {
      setSelectedMenu(activeTab.menuId);
      setSelectedKey(activeTab.routeUrl);
    } else if (savedKey) {
      setSelectedKey(savedKey);
    }

    setOpenKeys(savedOpenKeys);
  }, [tabs, location.pathname]);

  useEffect(() => {
    const handleScrollToTop = () => {
      window.scrollTo(0, 0);
      dispatch(closeNavigation());
    };

    handleScrollToTop();
  }, [location.pathname, dispatch]);

  // Fetch employee targets function
  const fetchEmployeeTargets = useCallback(async () => {
    try {
      console.log('📊 Fetching employee targets...');
      const resData = await dispatch(getData('/targets/employee/clock-in'));
      console.log('📊 Targets API response:', resData);
      if (resData?.code === 200 && resData.data?.hasTargets) {
        console.log('✅ Targets found:', resData.data.targets);
        setEmployeeTargets(resData.data.targets || []);
        setTargetPopupVisible(true);
      } else {
        console.log('ℹ️ No targets found or not morning time');
      }
    } catch (error) {
      console.error('❌ Error fetching employee targets:', error);
    }
  }, [dispatch]);

  // Listen for clock-in success socket event globally
  useEffect(() => {
    if (!socket) {
      console.log('⚠️ Socket not available for target popup');
      return;
    }

    const handleClockInSuccess = async (data?: any) => {
      console.log('🎯 Clock-in success event received in Navbar:', data);
      // Fetch targets when clock-in is successful
      await fetchEmployeeTargets();
    };

    // Also listen to all events for debugging
    const onAnyHandler = (eventName: string, ...args: any[]) => {
      console.log('📨 Received socket event:', eventName, args);
    };

    const setupListener = () => {
      console.log('👂 Navbar: Setting up clock-in-success socket listener');
      console.log('📡 Socket ID:', socket.id);
      console.log('📡 Socket connected:', socket.connected);

      socket.on('clock-in-success', handleClockInSuccess);
      socket.onAny(onAnyHandler);
    };

    // If socket is already connected, set up listener immediately
    if (socket.connected) {
      setupListener();
    } else {
      // Otherwise, wait for connection
      console.log('⚠️ Socket not connected, waiting for connection...');
      const onConnect = () => {
        console.log('✅ Socket connected, setting up listener');
        setupListener();
      };
      socket.on('connect', onConnect);
    }

    return () => {
      console.log('🔇 Navbar: Cleaning up socket listeners');
      socket.off('connect');
      socket.off('clock-in-success', handleClockInSuccess);
      socket.offAny();
    };
  }, [socket, fetchEmployeeTargets]);

  const logout = async () => {
    await dispatch(
      logoutUnAuthenticat(() => {
        localStorage.removeItem('selectedMenuKey');
        localStorage.removeItem('openMenuKeys');
        window.location.href = '/login';
      })
    );
  };

  const logOut = useCallback(() => {
    confirm({
      title: 'Are you sure you want to log out?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk() {
        logout();
      },
    });
  }, [logout]);

  const navigateToPlainPath = (path) => {
    localStorage.setItem('selectedMenuKey', 'profile');
    localStorage.setItem('openMenuKeys', JSON.stringify([]));
    setSelectedMenu(null);
    navigate(path)
  }

  const getItems = useCallback(() => {
    return [
      {
        key: '1',
        label: (
          <div key="profile" onClick={() => {
            navigateToPlainPath('/profile')
          }} className="navbar-item">
            View Profile
          </div>
        ),
        icon: <ProfileOutlined />,
      },
      {
        key: '2',
        label: (
          <div style={{ paddingLeft: 12 }} onClick={logOut}>
            Log Out
          </div>
        ),
        icon: <LogoutOutlined />,
      },
    ];
  }, [logOut, navigate]);

  const handleMenuClick = useCallback(
    (event) => {
      const menuId = event.item.props.itemID;
      const selectedTab = tabs.find((item) => item.menuId === menuId);

      if (!selectedTab) {
        navigate(event.key);
      }

      setSelectedKey(event.key);
      setSelectedMenu(menuId);
      localStorage.setItem('selectedMenuKey', event.key);
    },
    [navigate, tabs]
  );

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
    localStorage.setItem('openMenuKeys', JSON.stringify(keys));
  };

  const getSideBarMenu = () => {
    return (
      <Menu
        theme="light"
        onClick={handleMenuClick}
        selectedKeys={[selectedKey]}
        mode="inline"
        inlineCollapsed={collapsed}
        onOpenChange={handleOpenChange}
        openKeys={openKeys}
      >
        {navigations &&
          navigations.map((ele) =>
            !ele.list ? (
              <Menu.Item key={ele.routeUrl} itemID={ele._id} icon={getIcon({ name: ele.icon })}>
                {ele.name}
              </Menu.Item>
            ) : (
              <Menu.SubMenu key={ele.parentMenu} icon={getIcon({ name: ele.icon })} title={ele.parentMenu}>
                {ele.list.map((parentMn) =>
                  !parentMn.list ? (
                    <Menu.Item key={parentMn.routeUrl} itemID={parentMn._id} icon={getIcon({ name: parentMn.icon })}>
                      {parentMn.name}
                    </Menu.Item>
                  ) : (
                    <Menu.SubMenu
                      key={parentMn.subMenu}
                      icon={getIcon({ name: parentMn.icon })}
                      title={parentMn.subMenu}
                    >
                      {parentMn.list.map((subMn) => (
                        <Menu.Item key={subMn.routeUrl} itemID={subMn._id} icon={getIcon({ name: subMn.icon })}>
                          {subMn.name}
                        </Menu.Item>
                      ))}
                    </Menu.SubMenu>
                  )
                )}
              </Menu.SubMenu>
            )
          )}
      </Menu>
    );
  };

  if (invalidRoutes.includes(location.pathname)) {
    return null;
  }

  const renderTab = () => {
    const currentTab = tabs?.find((item) => item.menuId === selectedMenu);
    if (!currentTab) return null;

    return <TabsRender selectedKey={selectedKey} selectedMenu={selectedMenu} />;
  };

  return (
    <Layout hasSider>
      <Sider
        theme="light"
        className="layout_sidebar"
        style={{
          insetInlineStart: 0,
          width: collapsed ? 80 : 200,
        }}
        trigger={null}
        collapsible
        collapsed={collapsed}
      >
        <div
          className="navbar-start layout_logo"
          style={{
            insetInlineStart: 0,
            width: collapsed ? 80 : 200,
          }}
        >
          {!collapsed ? (
            <img src={require('../../assets/logo/logo.png')} alt="" className="logo-admin" />
          ) : (
            <img src={require('../../assets/logo/short_icon.png')} alt="" className="logo-admin short_logo" />
          )}
        </div>
        <div className="menu_render">{isAuthenticated && getSideBarMenu()}</div>
      </Sider>
      <Layout
        style={{
          marginInlineStart: collapsed ? 80 : 200,
          transition: 'margin-inline-start 0.2s',
        }}
      >
        <Header style={{ left: collapsed ? 80 : 200 }} className="header_view">
          <BenchPolicyModal open={isBenchPolicy} onClose={() => setBenchPolicy(false)} />

          <div className="header_left_rigth items-center">
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                margin: 'auto 0px',
              }}
            />
            {user?.isOnBench && <div className="text-red-500 text-center leading-none w-full">
              <>
                You have been on the bench for {moment().diff(moment(user?.dateOfJoining), 'days')} days. Please review the{' '}
                <Button className='p-0' type='link' onClick={() => setBenchPolicy(true)}>bench policy</Button> and try to get onboarded to a client project.
              </>
            </div>}
            <div className="user_profile_avtar">
              <Dropdown menu={{ items: getItems() }}>
                <div>
                  {isAuthenticated && user.firstName && (
                    <Space direction="horizontal" className="user_profile">
                      <Avatar size="large"
                        src={`${process.env.FILE_URL}${user?.avatar}`}
                        shape="circle">
                        {!user?.avatar && user.firstName[0]}
                        {!user?.avatar && user.lastName[0]}
                      </Avatar>
                      <div className="name_and_designation">
                        <div>{user.firstName}</div>
                        {user.designation && <div>{truncate(user.designation)}</div>}
                      </div>
                    </Space>
                  )}
                </div>
              </Dropdown>
            </div>
          </div>
        </Header>
        <Content className="app_content">
          <Space direction="vertical" style={{ width: '100%' }}>
            {renderTab()}
            {routes}
          </Space>
        </Content>
      </Layout>

      {/* Global Target Popup - appears on any page when clock-in is successful */}
      <TargetPopup
        visible={targetPopupVisible}
        targets={employeeTargets}
        onClose={() => setTargetPopupVisible(false)}
      />
    </Layout>
  );
};

export default Navbar;
